<template>
  <master-layout :enable-scroll-event="true" :show-footer="false" :full-width="true">
    <div v-if="loading" class="w-full min-h-screen bg-[#F6F4F0] relative">
      <Loading />
    </div>
    <PinOverlay
      v-else-if="!isChecking && showPinOverlay && !hideOverlay"
      :open="true"
      :mode="overlayMode"
      :variant="'verify'"
      :locked-until="lockedUntil"
      @verify="onPinVerify"
      @forgot="goToForgotPin"
      @cancel="back"
      @lock-expired="handleLockExpired"
      ref="pinOverlay"
    />

    <div v-show="gateOpen" class="bg-[#F6F4F0] w-full min-h-screen pb-[64px]">
      <BackButton class="w-full px-5 md:px-6 lg:px-8 mb-[8px]" :customBack="back" />

      <div class="w-full md:max-w-2xl md:mx-auto">
        <Loading v-if="!dataLoaded" />

        <template v-else>
          <MyGivingHeader class="px-5 md:px-6 lg:px-8 -mt-[24px]" />

          <div class="px-5 md:px-6 lg:px-8">
            <BfPledgeBanner
              v-if="
                showBfPledgeBanner && givingResources[0]?.showBfBanner && !needsGivingVerification
              "
              :image-url="givingResources[0]?.bfBannerThumbnail?.url"
              :url="givingResources[0]?.bfBannerURL"
              :has-pledge="hasBfPledge"
              class="mt-[24px]"
            />
            <!-- Giving per login: a login of a member with several confirms the
                 personal id once before it sees any giving records. -->
            <VerifyPersonalIdCard
              v-if="needsGivingVerification"
              class="mt-[24px]"
              @verified="onPersonalIdVerified"
            />
            <TitheHistoryCard
              v-else
              class="mt-[24px]"
              :tithe-years="titheYears"
              :bf-pledge-years="bfPledgeYears"
              :tithing-state="tithingState"
            ></TitheHistoryCard>
            <DsAlert
              v-if="hasNoPersonalId"
              variant="info"
              class="mt-[16px]"
              data-test="no-personal-id-notice"
            >
              Add your date of birth and the last 4 characters of your NRIC in your profile to see
              your giving here.
              <DsLink size="sm" underline="always" @click="goToProfile">Go to my profile</DsLink>
            </DsAlert>
            <BfBanner
              v-if="
                givingResources[0]?.showBfBanner && (!showBfPledgeBanner || needsGivingVerification)
              "
              :image-url="givingResources[0]?.bfBannerThumbnail.url"
              :url="givingResources[0]?.bfBannerURL"
              class="mt-[16px]"
            ></BfBanner>

            <!-- Recurring Giving Link -->
            <a
              class="h-[53px] md:h-[64px] lg:h-[72px] bg-[#F0EAE0] flex flex-row justify-between items-center rounded-[12px] border-[1px] border-[#D5CFC8] mt-[16px] cursor-pointer px-[16px] md:px-[20px] lg:px-[24px]"
              @click="gotoRecurring"
            >
              <p class="font-[600] text-[1rem] md:text-[1.75rem] lg:text-[1.875rem]">
                Set up recurring giving here!
              </p>
              <img
                src="assets/icons/arrow-right.svg"
                alt="Arrow"
                class="w-[16px] h-[16px] md:w-[20px] md:h-[20px] lg:w-[24px] lg:h-[24px]"
              />
            </a>
          </div>

          <div class="px-5 md:px-6 lg:px-8">
            <!-- <ResourceSection class="-mr-[20px] pb-0"></ResourceSection> -->
            <h1
              class="text-[#3D3A35] font-[900] text-[1.125rem] md:text-[1.25rem] lg:text-[1.375rem] leading-[1.5rem] md:leading-[1.625rem] lg:leading-[1.75rem] mb-[16px] mt-[32px]"
            >
              Need Help?
            </h1>
            <AdditionalInfo />
          </div>
        </template>
      </div>
    </div>
  </master-layout>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import MasterLayout from '@/components/MasterLayout.vue';
import PinOverlay from '@/components/MyGiving/Common/Pin.vue';
import MyGivingHeader from '@/components/MyGiving/LandingPage/Header.vue';
import TitheHistoryCard from '@/components/MyGiving/LandingPage/TitheHistoryCard.vue';
import BfBanner from '@/components/MyGiving/LandingPage/BannerBF.vue';
import BfPledgeBanner from '@/components/MyGiving/LandingPage/BfPledgeBanner.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { BUILDING_FUND_PLEDGE_FLAG } from '@/constants/MyGiving/building-fund-pledge';
import { isAdultByAge } from '@/utils/building-fund-pledge';
import { GIVING_LOGIN_STATES } from '@/utils/giving-login';
import AdditionalInfo from '@/components/MyGiving/Common/AdditionalInfo.vue';
import Loading from '@/components/common/LoadingState.vue';
import BackButton from '@/components/common/BackButton.vue';
import VerifyPersonalIdCard from '@/components/MyGiving/LandingPage/VerifyPersonalIdCard.vue';
import { Alert as DsAlert, Link as DsLink } from '@team-hogc/hogc-one-design-system';

export default {
  name: 'MyGivingLanding',

  components: {
    MasterLayout,
    PinOverlay,
    MyGivingHeader,
    TitheHistoryCard,
    BfBanner,
    BfPledgeBanner,
    AdditionalInfo,
    Loading,
    BackButton,
    VerifyPersonalIdCard,
    DsAlert,
    DsLink,
  },

  data() {
    return {
      isChecking: true,
      gateOpen: false,
      hideOverlay: false,

      overlayMode: 'verify',
      attemptsLeft: 3,
      lockedUntil: null,

      lockTimer: null,
      loading: false,
      dataLoaded: false,
      // Between ionViewWillEnter and ionViewDidLeave. See the `$route.query.redirect`
      // watcher for why it matters.
      isViewActive: false,
    };
  },

  computed: {
    ...mapState('myGiving', ['tithingState', 'titheYears', 'bfPledgeYears', 'givingResources']),
    ...mapGetters('buildingFundPledge', { hasBfPledge: 'hasPledge' }),
    ...mapGetters('myGiving', ['needsGivingVerification', 'hasNoPersonalId']),

    showPinOverlay() {
      const isOnLandingPage = this.$route.path === '/my-giving';
      return !this.gateOpen && isOnLandingPage;
    },

    // Read inside the computed rather than at import time, as the Courses pages
    // do, so specs that mock the flags composable are not tripped by module load.
    // Under-18s see no banner (as giving-api hides their history tiles), but the
    // pledge routes stay open to them: deep links are intended.
    showBfPledgeBanner() {
      const { isEnabled } = useFeatureFlags();
      return isEnabled(BUILDING_FUND_PLEDGE_FLAG) && isAdultByAge(this.$store.state.profile);
    },
  },

  watch: {
    // Member-level flags load after sign-in, so the flag can come on after
    // fetchPageData has run without it. Load the pledge then, for the banner label.
    showBfPledgeBanner(on) {
      if (on && this.dataLoaded && !this.needsGivingVerification) this.fetchBfPledge();
    },

    // The PIN guard sends a sub-page it refuses back here with `?redirect=`. When this
    // page is the one already showing, that is only a query change: Ionic does not
    // run ionViewWillEnter again, so nothing checked the PIN and nothing navigated,
    // and Give now (or a BF2026 QR deep link) looked stuck. It happens after the app
    // was backgrounded, since `pause` clears the PIN session while the page stays up.
    '$route.query.redirect'(redirect) {
      this.onRedirectWhileOpen(redirect);
    },
  },

  async ionViewWillEnter() {
    this.isViewActive = true;
    await this.enterGate();
  },

  ionViewDidEnter() {
    // Scroll to top if returning from homepage
    const previousRoute = localStorage.getItem('route:previous');
    if (previousRoute === '/home') {
      this.$nextTick(() => {
        setTimeout(() => {
          this.scrollToTop();
        }, 150);
      });
    }
  },

  ionViewDidLeave() {
    console.log('Landing: ionViewDidLeave');
    this.isViewActive = false;
    this.resetPageState();
  },

  beforeUnmount() {
    this.clearLockTimer();
  },

  methods: {
    // Re-run the gate for a redirect that arrived while this page was open: straight
    // on if the server's PIN window still holds, else the PIN overlay, then on. A
    // page entering normally runs the gate from ionViewWillEnter instead.
    onRedirectWhileOpen(redirect) {
      if (!this.isViewActive || !this.isSafeMyGivingRedirect(redirect)) return null;
      return this.enterGate();
    },

    async enterGate() {
      this.loading = true;
      this.isChecking = true;
      this.gateOpen = false;
      this.hideOverlay = false;
      this.dataLoaded = false;

      const state = await this.checkPinState();
      this.loading = false;

      if (state?.state === 'pin_not_set') {
        this.redirectToCreatePin();
        return;
      }

      this.applyStateFromServer(state);
      this.isChecking = false;

      if (this.gateOpen && this.consumePendingRedirect()) {
        return;
      }

      // Start loading data in background when PIN overlay is shown
      // This allows data to load while user is entering PIN
      if (state?.state === 'pin_exists' || state?.state === 'pin_locked') {
        this.fetchPageData();
      }

      // If gate is already open (PIN previously verified), wait for data
      if (this.gateOpen) {
        await this.$nextTick();
        await this.fetchPageData();
      }
    },

    async checkPinState() {
      try {
        const state = await this.$store.dispatch('myGiving/getPinState');
        console.log('Landing: PIN state:', state);
        return state;
      } catch (error) {
        console.error('Landing: Error checking PIN state:', error);
        return null;
      }
    },

    applyStateFromServer(state) {
      const pinState = state?.state ?? null;
      this.lockedUntil = state?.lockedUntil ? new Date(state.lockedUntil) : null;

      if (pinState === 'pin_locked') {
        console.log('Landing: Applying locked state');
        this.overlayMode = 'locked';
        this.gateOpen = false;
        this.attemptsLeft = 0;
        this.startLockTimer();
        return;
      }

      if (pinState === 'pin_exists') {
        console.log('Landing: Applying exists state');
        this.overlayMode = 'verify';
        this.gateOpen = false;
        this.attemptsLeft = 3;
        return;
      }

      if (pinState === 'pin_verified' || pinState === 'pin_matched') {
        console.log('Landing: Applying verified state - opening gate');
        this.overlayMode = 'verify';
        this.gateOpen = true;
        this.dataLoaded = false;
        this.attemptsLeft = 3;
        return;
      }

      this.overlayMode = 'verify';
      this.gateOpen = false;
    },

    async onPinVerify(pin) {
      try {
        const res = await this.$store.dispatch('myGiving/verifyPin', { pin });

        const data = res?.data || res;

        if (data?.state === 'pin_matched') {
          this.gateOpen = true;
          this.dataLoaded = false;
          this.attemptsLeft = 3;

          if (this.consumePendingRedirect()) {
            return;
          }

          await this.fetchPageData();
          return;
        }

        if (data?.state === 'pin_incorrect') {
          this.attemptsLeft = data.attemptsLeft ?? Math.max(0, this.attemptsLeft - 1);
          const message = `Wrong PIN.  You have ${this.attemptsLeft} ${
            this.attemptsLeft === 1 ? 'attempt' : 'attempts'
          } remaining.`;
          this.$refs.pinOverlay?.showError(message);
          return;
        }

        if (data?.state === 'pin_locked') {
          this.overlayMode = 'locked';
          this.lockedUntil = data.lockedUntil ? new Date(data.lockedUntil) : null;
          this.attemptsLeft = 0;
          this.gateOpen = false;
          this.startLockTimer();
          return;
        }

        this.$refs.pinOverlay?.showError('Something went wrong. Please try again.');
      } catch (error) {
        // The message only: the whole error carries the request body, the PIN.
        console.error('Landing: Verify error:', error?.message);
        this.$refs.pinOverlay?.showError('Something went wrong. Please try again.');
      }
    },

    consumePendingRedirect() {
      const redirect = this.$route.query.redirect;
      if (!this.isSafeMyGivingRedirect(redirect)) {
        return false;
      }

      this.$router.replace(redirect);
      return true;
    },

    isSafeMyGivingRedirect(redirect) {
      return (
        typeof redirect === 'string' &&
        redirect.startsWith('/my-giving/') &&
        !redirect.startsWith('/my-giving/pin/')
      );
    },

    startLockTimer() {
      if (!this.lockedUntil) return;
      this.clearLockTimer();

      const tick = () => {
        const ms = this.lockedUntil - new Date();
        if (ms <= 0) {
          this.handleLockExpired();
          return;
        }
        this.lockTimer = setTimeout(tick, 1000);
      };

      tick();
    },

    clearLockTimer() {
      if (this.lockTimer) {
        clearTimeout(this.lockTimer);
        this.lockTimer = null;
      }
    },

    async handleLockExpired() {
      console.log('Landing: Lock expired - refreshing PIN state from server');
      this.clearLockTimer();

      try {
        const state = await this.checkPinState();

        if (state) {
          this.applyStateFromServer(state);
          console.log('Landing: PIN state refreshed after lock expiration');
        } else {
          // Fallback to local reset if server call fails
          this.overlayMode = 'verify';
          this.lockedUntil = null;
          this.attemptsLeft = 3;
          console.warn('Landing: Using fallback state reset after lock expiration');
        }
      } catch (error) {
        console.error('Landing: Error refreshing PIN state after lock expiration:', error);
        // Fallback to local reset
        this.overlayMode = 'verify';
        this.lockedUntil = null;
        this.attemptsLeft = 3;
      }
    },

    goToForgotPin() {
      this.hideOverlay = true;
      this.$nextTick(() => {
        this.$router.push({ path: '/my-giving/pin/forgot-pin' });
      });
    },

    back() {
      console.log('Landing: Navigating back');
      this.hideOverlay = true;
      this.$store.dispatch('myGiving/endPinSession');
      this.$router.replace('/home');
    },

    gotoRecurring() {
      this.$router.push('/my-giving/setup-recurring-start');
    },

    goToProfile() {
      this.$router.push('/profile');
    },

    // The check passed: giving-api serves this login's records from the next call.
    async onPersonalIdVerified() {
      this.dataLoaded = false;
      await this.fetchPageData();
    },

    // A pending destination (a deep link such as the BF2026 QR's Give now) goes
    // along, so it opens once the new PIN is set and entered.
    redirectToCreatePin() {
      this.isChecking = false;
      const { redirect } = this.$route.query;
      const query = this.isSafeMyGivingRedirect(redirect) ? { redirect } : {};
      this.$router.replace({ path: '/my-giving/pin/create-pin', query });
    },

    resetPageState() {
      this.gateOpen = false;
      this.isChecking = false;
      this.hideOverlay = false;
      this.dataLoaded = false;
    },

    // Only decides where the BF2026 banner goes (the form, or the pledge), and this runs
    // behind the PIN overlay, so a failure is quiet. Never rejects.
    fetchBfPledge() {
      return this.$store.dispatch('buildingFundPledge/fetchPledge', { quiet: true });
    },

    async fetchPageData() {
      console.log('Landing: Fetching page data...');

      try {
        // Never rejects: an hogc-one-api without it leaves the state unknown, as before.
        const [, loginState] = await Promise.all([
          this.$store.dispatch('myGiving/getGivingResources'),
          this.$store.dispatch('myGiving/getLoginGivingState'),
        ]);
        await this.$store.dispatch('myGiving/getLinkedBankAccounts');

        // A login that has not confirmed the personal id can give but sees no
        // records, so none are asked for: the verify step takes their place.
        if (loginState?.state === GIVING_LOGIN_STATES.VERIFICATION_REQUIRED) {
          this.dataLoaded = true;
          return;
        }

        const [bfYearResponse, titheYearResponse] = await Promise.all([
          this.$store.dispatch('myGiving/getBFPledgeYears'),
          this.$store.dispatch('myGiving/getTitheYears'),
          this.showBfPledgeBanner ? this.fetchBfPledge() : null,
        ]);

        // Treat API errors as "no data" - show page with State 0 (no history)
        const bfYears = bfYearResponse?.data ?? [];
        const titheYears = titheYearResponse?.data ?? [];

        await this.$store.dispatch('myGiving/getTithingBfState', {
          titheYear: titheYears,
          bfYear: bfYears,
        });

        this.dataLoaded = true;
        console.log('Landing: Page data loaded, dataLoaded set to true');
      } catch (error) {
        console.error('Landing: Error fetching page data:', error);
        // Treat error as "no data" - show page with State 0
        await this.$store.dispatch('myGiving/getTithingBfState', {
          titheYear: [],
          bfYear: [],
        });
        this.dataLoaded = true;
      }
    },

    async scrollToTop() {
      try {
        const contentElement = document.getElementById('ion-content');
        if (contentElement && typeof contentElement.scrollToTop === 'function') {
          await contentElement.scrollToTop();
          console.log('[Landing] Scrolled to top');
        } else {
          console.warn(
            '[Landing] ion-content element not found or scrollToTop method not available',
          );
        }
      } catch (error) {
        console.warn('[Landing] Error scrolling to top:', error);
      }
    },
  },
};
</script>
